// Core Workflow Execution Engine

import { 
  WorkflowDefinition, 
  WorkflowExecution, 
  WorkflowNode, 
  WorkflowEdge,
  ExecutionStatus, 
  ExecutionContext,
  ExecutionLog,
  ExecutionError,
  NodeType
} from '../types/workflow';
import { ConnectorManager } from './ConnectorManager';
import { EventEmitter } from 'events';

export class WorkflowEngine extends EventEmitter {
  private connectorManager: ConnectorManager;
  private executions: Map<string, WorkflowExecution>;
  private nodeExecutors: Map<NodeType, NodeExecutor>;

  constructor(connectorManager: ConnectorManager) {
    super();
    this.connectorManager = connectorManager;
    this.executions = new Map();
    this.nodeExecutors = new Map();
    this.initializeNodeExecutors();
  }

  private initializeNodeExecutors() {
    this.nodeExecutors.set('action', new ActionNodeExecutor(this.connectorManager));
    this.nodeExecutors.set('condition', new ConditionNodeExecutor());
    this.nodeExecutors.set('loop', new LoopNodeExecutor(this));
    this.nodeExecutors.set('transform', new TransformNodeExecutor());
    this.nodeExecutors.set('wait', new WaitNodeExecutor());
    this.nodeExecutors.set('approval', new ApprovalNodeExecutor());
    this.nodeExecutors.set('notification', new NotificationNodeExecutor(this.connectorManager));
    this.nodeExecutors.set('subflow', new SubflowNodeExecutor(this));
  }

  async executeWorkflow(
    workflow: WorkflowDefinition, 
    inputData: any = {}, 
    context: Partial<ExecutionContext> = {}
  ): Promise<WorkflowExecution> {
    const execution: WorkflowExecution = {
      id: this.generateExecutionId(),
      workflowId: workflow.id,
      status: 'pending',
      startTime: new Date().toISOString(),
      inputData,
      logs: [],
      metrics: {
        duration: 0,
        nodesExecuted: 0,
        totalNodes: workflow.nodes.length,
        memoryUsage: 0,
        cpuUsage: 0,
        apiCalls: 0
      },
      context: {
        userId: context.userId || 'system',
        triggerType: context.triggerType || 'manual',
        environment: context.environment || 'production',
        variables: { ...workflow.variables, ...context.variables },
        secrets: context.secrets || {}
      }
    };

    this.executions.set(execution.id, execution);
    this.emit('execution.started', execution);

    try {
      execution.status = 'running';
      this.log(execution, 'info', 'Workflow execution started', { workflowId: workflow.id });

      // Find start nodes (nodes with no incoming edges)
      const startNodes = this.findStartNodes(workflow);
      
      if (startNodes.length === 0) {
        throw new Error('No start nodes found in workflow');
      }

      // Initialize execution state
      const executionState = new ExecutionState(inputData);

      // Execute the workflow
      await this.executeNodes(workflow, startNodes, executionState, execution);

      execution.status = 'completed';
      execution.endTime = new Date().toISOString();
      execution.outputData = executionState.getData();
      execution.metrics.duration = Date.parse(execution.endTime) - Date.parse(execution.startTime);

      this.log(execution, 'info', 'Workflow execution completed successfully');
      this.emit('execution.completed', execution);

    } catch (error) {
      execution.status = 'failed';
      execution.endTime = new Date().toISOString();
      execution.error = {
        code: 'EXECUTION_ERROR',
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      };

      this.log(execution, 'error', `Workflow execution failed: ${error.message}`, { error });
      this.emit('execution.failed', execution);
    }

    return execution;
  }

  async pauseExecution(executionId: string): Promise<void> {
    const execution = this.executions.get(executionId);
    if (!execution) {
      throw new Error(`Execution ${executionId} not found`);
    }

    execution.status = 'paused';
    this.log(execution, 'info', 'Workflow execution paused');
    this.emit('execution.paused', execution);
  }

  async resumeExecution(executionId: string): Promise<void> {
    const execution = this.executions.get(executionId);
    if (!execution) {
      throw new Error(`Execution ${executionId} not found`);
    }

    if (execution.status !== 'paused') {
      throw new Error(`Cannot resume execution in status: ${execution.status}`);
    }

    execution.status = 'running';
    this.log(execution, 'info', 'Workflow execution resumed');
    this.emit('execution.resumed', execution);
  }

  async cancelExecution(executionId: string): Promise<void> {
    const execution = this.executions.get(executionId);
    if (!execution) {
      throw new Error(`Execution ${executionId} not found`);
    }

    execution.status = 'cancelled';
    execution.endTime = new Date().toISOString();
    this.log(execution, 'info', 'Workflow execution cancelled');
    this.emit('execution.cancelled', execution);
  }

  getExecution(executionId: string): WorkflowExecution | undefined {
    return this.executions.get(executionId);
  }

  private async executeNodes(
    workflow: WorkflowDefinition,
    nodes: WorkflowNode[],
    state: ExecutionState,
    execution: WorkflowExecution
  ): Promise<void> {
    for (const node of nodes) {
      if (execution.status !== 'running') {
        break; // Stop execution if paused or cancelled
      }

      await this.executeNode(workflow, node, state, execution);
      execution.metrics.nodesExecuted++;

      // Find next nodes to execute
      const nextNodes = this.findNextNodes(workflow, node, state);
      if (nextNodes.length > 0) {
        await this.executeNodes(workflow, nextNodes, state, execution);
      }
    }
  }

  private async executeNode(
    workflow: WorkflowDefinition,
    node: WorkflowNode,
    state: ExecutionState,
    execution: WorkflowExecution
  ): Promise<void> {
    this.log(execution, 'info', `Executing node: ${node.data.label}`, { nodeId: node.id, nodeType: node.type });

    try {
      const executor = this.nodeExecutors.get(node.type);
      if (!executor) {
        throw new Error(`No executor found for node type: ${node.type}`);
      }

      const nodeContext = {
        node,
        workflow,
        execution,
        state
      };

      const result = await executor.execute(nodeContext);
      
      // Update state with node output
      if (result) {
        state.setNodeOutput(node.id, result);
      }

      this.log(execution, 'info', `Node executed successfully: ${node.data.label}`, { 
        nodeId: node.id, 
        output: result 
      });

    } catch (error) {
      this.log(execution, 'error', `Node execution failed: ${node.data.label}`, { 
        nodeId: node.id, 
        error: error.message 
      });

      // Handle error based on workflow settings
      if (workflow.settings?.errorHandling?.onError === 'stop') {
        throw error;
      } else if (workflow.settings?.errorHandling?.onError === 'continue') {
        // Continue with next nodes
        state.setNodeOutput(node.id, { error: error.message });
      }
    }
  }

  private findStartNodes(workflow: WorkflowDefinition): WorkflowNode[] {
    const nodesWithIncomingEdges = new Set(workflow.edges.map(edge => edge.target));
    return workflow.nodes.filter(node => !nodesWithIncomingEdges.has(node.id));
  }

  private findNextNodes(
    workflow: WorkflowDefinition,
    currentNode: WorkflowNode,
    state: ExecutionState
  ): WorkflowNode[] {
    const outgoingEdges = workflow.edges.filter(edge => edge.source === currentNode.id);
    const nextNodes: WorkflowNode[] = [];

    for (const edge of outgoingEdges) {
      // Check edge conditions
      if (this.evaluateEdgeConditions(edge, state)) {
        const nextNode = workflow.nodes.find(node => node.id === edge.target);
        if (nextNode) {
          nextNodes.push(nextNode);
        }
      }
    }

    return nextNodes;
  }

  private evaluateEdgeConditions(edge: WorkflowEdge, state: ExecutionState): boolean {
    if (!edge.conditions || edge.conditions.length === 0) {
      return true; // No conditions means always proceed
    }

    // Implement condition evaluation logic
    return edge.conditions.every(condition => {
      const value = state.getValue(condition.field);
      return this.evaluateCondition(value, condition.operator, condition.value);
    });
  }

  private evaluateCondition(value: any, operator: string, expectedValue: any): boolean {
    switch (operator) {
      case 'equals':
        return value === expectedValue;
      case 'not_equals':
        return value !== expectedValue;
      case 'greater_than':
        return value > expectedValue;
      case 'less_than':
        return value < expectedValue;
      case 'contains':
        return String(value).includes(String(expectedValue));
      case 'regex':
        return new RegExp(expectedValue).test(String(value));
      default:
        return false;
    }
  }

  private log(
    execution: WorkflowExecution,
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    metadata?: any
  ): void {
    const log: ExecutionLog = {
      id: this.generateLogId(),
      executionId: execution.id,
      level,
      message,
      timestamp: new Date().toISOString(),
      metadata
    };

    execution.logs.push(log);
    this.emit('execution.log', log);
  }

  private generateExecutionId(): string {
    return `exec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateLogId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Execution State Management
class ExecutionState {
  private data: Record<string, any>;
  private nodeOutputs: Map<string, any>;

  constructor(initialData: any = {}) {
    this.data = { ...initialData };
    this.nodeOutputs = new Map();
  }

  getData(): Record<string, any> {
    return { ...this.data };
  }

  getValue(path: string): any {
    const keys = path.split('.');
    let current = this.data;

    for (const key of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = current[key];
    }

    return current;
  }

  setValue(path: string, value: any): void {
    const keys = path.split('.');
    let current = this.data;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }

  setNodeOutput(nodeId: string, output: any): void {
    this.nodeOutputs.set(nodeId, output);
    this.setValue(`nodes.${nodeId}`, output);
  }

  getNodeOutput(nodeId: string): any {
    return this.nodeOutputs.get(nodeId);
  }
}

// Node Executor Interfaces and Implementations
interface NodeExecutor {
  execute(context: NodeExecutionContext): Promise<any>;
}

interface NodeExecutionContext {
  node: WorkflowNode;
  workflow: WorkflowDefinition;
  execution: WorkflowExecution;
  state: ExecutionState;
}

class ActionNodeExecutor implements NodeExecutor {
  constructor(private connectorManager: ConnectorManager) {}

  async execute(context: NodeExecutionContext): Promise<any> {
    const { node } = context;
    
    if (!node.connectorId || !node.actionId) {
      throw new Error('Action node requires connector and action configuration');
    }

    const connector = await this.connectorManager.getConnector(node.connectorId);
    const result = await connector.executeAction(node.actionId, node.configuration);
    
    return result;
  }
}

class ConditionNodeExecutor implements NodeExecutor {
  async execute(context: NodeExecutionContext): Promise<any> {
    const { node, state } = context;
    const { condition } = node.configuration;

    if (!condition) {
      throw new Error('Condition node requires condition configuration');
    }

    const value = state.getValue(condition.field);
    const result = this.evaluateCondition(value, condition.operator, condition.value);

    return { result, value, condition };
  }

  private evaluateCondition(value: any, operator: string, expectedValue: any): boolean {
    switch (operator) {
      case 'equals':
        return value === expectedValue;
      case 'not_equals':
        return value !== expectedValue;
      case 'greater_than':
        return value > expectedValue;
      case 'less_than':
        return value < expectedValue;
      case 'contains':
        return String(value).includes(String(expectedValue));
      case 'regex':
        return new RegExp(expectedValue).test(String(value));
      default:
        return false;
    }
  }
}

class LoopNodeExecutor implements NodeExecutor {
  constructor(private engine: WorkflowEngine) {}

  async execute(context: NodeExecutionContext): Promise<any> {
    const { node, workflow, state, execution } = context;
    const { loopType, collection, condition, maxIterations = 1000 } = node.configuration;

    let iterations = 0;
    const results: any[] = [];

    if (loopType === 'forEach') {
      const items = state.getValue(collection);
      if (!Array.isArray(items)) {
        throw new Error('ForEach loop requires an array collection');
      }

      for (const item of items) {
        if (iterations >= maxIterations) break;

        // Set current item in state
        state.setValue('$item', item);
        state.setValue('$index', iterations);

        // Execute loop body (would need to identify loop body nodes)
        iterations++;
      }
    } else if (loopType === 'while') {
      while (iterations < maxIterations) {
        const shouldContinue = this.evaluateCondition(condition, state);
        if (!shouldContinue) break;

        // Execute loop body
        iterations++;
      }
    }

    return { iterations, results };
  }

  private evaluateCondition(condition: any, state: ExecutionState): boolean {
    // Implement condition evaluation
    return true; // Simplified
  }
}

class TransformNodeExecutor implements NodeExecutor {
  async execute(context: NodeExecutionContext): Promise<any> {
    const { node, state } = context;
    const { transformations } = node.configuration;

    let result = state.getData();

    for (const transformation of transformations || []) {
      result = await this.applyTransformation(result, transformation);
    }

    return result;
  }

  private async applyTransformation(data: any, transformation: any): Promise<any> {
    switch (transformation.type) {
      case 'map':
        return this.mapFields(data, transformation.mapping);
      case 'filter':
        return this.filterData(data, transformation.condition);
      case 'aggregate':
        return this.aggregateData(data, transformation.operation);
      default:
        return data;
    }
  }

  private mapFields(data: any, mapping: Record<string, string>): any {
    const result: any = {};
    
    for (const [targetPath, sourcePath] of Object.entries(mapping)) {
      const value = this.getValue(data, sourcePath);
      this.setValue(result, targetPath, value);
    }

    return result;
  }

  private filterData(data: any[], condition: any): any[] {
    return data.filter(item => this.evaluateCondition(item, condition));
  }

  private aggregateData(data: any[], operation: any): any {
    switch (operation.type) {
      case 'sum':
        return data.reduce((sum, item) => sum + item[operation.field], 0);
      case 'count':
        return data.length;
      case 'average':
        return data.reduce((sum, item) => sum + item[operation.field], 0) / data.length;
      default:
        return data;
    }
  }

  private getValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  private setValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }

  private evaluateCondition(item: any, condition: any): boolean {
    // Simplified condition evaluation
    return true;
  }
}

class WaitNodeExecutor implements NodeExecutor {
  async execute(context: NodeExecutionContext): Promise<any> {
    const { node } = context;
    const { duration, unit = 'seconds' } = node.configuration;

    const milliseconds = this.convertToMilliseconds(duration, unit);
    
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({ waited: duration, unit });
      }, milliseconds);
    });
  }

  private convertToMilliseconds(duration: number, unit: string): number {
    switch (unit) {
      case 'milliseconds':
        return duration;
      case 'seconds':
        return duration * 1000;
      case 'minutes':
        return duration * 60 * 1000;
      case 'hours':
        return duration * 60 * 60 * 1000;
      default:
        return duration * 1000;
    }
  }
}

class ApprovalNodeExecutor implements NodeExecutor {
  async execute(context: NodeExecutionContext): Promise<any> {
    const { node, execution } = context;
    const { approvers, message, timeout } = node.configuration;

    // In a real implementation, this would send approval requests
    // and wait for responses via external API or webhook
    
    return new Promise((resolve, reject) => {
      // Simulate approval process
      setTimeout(() => {
        resolve({ 
          approved: true, 
          approver: approvers[0], 
          timestamp: new Date().toISOString() 
        });
      }, 5000);
    });
  }
}

class NotificationNodeExecutor implements NodeExecutor {
  constructor(private connectorManager: ConnectorManager) {}

  async execute(context: NodeExecutionContext): Promise<any> {
    const { node } = context;
    const { channel, message, recipients } = node.configuration;

    // Use appropriate connector for notification (email, Slack, Teams, etc.)
    const connector = await this.connectorManager.getConnector(channel);
    const result = await connector.executeAction('send_notification', {
      message,
      recipients
    });

    return result;
  }
}

class SubflowNodeExecutor implements NodeExecutor {
  constructor(private engine: WorkflowEngine) {}

  async execute(context: NodeExecutionContext): Promise<any> {
    const { node, state } = context;
    const { subflowId, inputMapping, outputMapping } = node.configuration;

    // Load subflow definition
    const subworkflow = await this.loadSubworkflow(subflowId);
    
    // Map input data
    const subflowInput = this.mapInput(state.getData(), inputMapping);
    
    // Execute subflow
    const subexecution = await this.engine.executeWorkflow(subworkflow, subflowInput);
    
    // Map output data
    const mappedOutput = this.mapOutput(subexecution.outputData, outputMapping);
    
    return mappedOutput;
  }

  private async loadSubworkflow(subflowId: string): Promise<WorkflowDefinition> {
    // In a real implementation, this would load from database or API
    throw new Error('Subflow loading not implemented');
  }

  private mapInput(data: any, mapping: Record<string, string>): any {
    const result: any = {};
    for (const [targetField, sourceField] of Object.entries(mapping)) {
      result[targetField] = this.getValue(data, sourceField);
    }
    return result;
  }

  private mapOutput(data: any, mapping: Record<string, string>): any {
    const result: any = {};
    for (const [targetField, sourceField] of Object.entries(mapping)) {
      result[targetField] = this.getValue(data, sourceField);
    }
    return result;
  }

  private getValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }
}